import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { arrayBufferToBase64url, base64ToUint8Array } from '../../lib/utils';
import { useAuthStore } from '../../store';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasskey, setShowPasskey] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  
  const handlePasswordLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const normalizedEmail = email.trim();
      const res = await api.auth.login({ email: normalizedEmail, password });
      setAuth(res.token, res.user);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasskeyLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      const normalizedEmail = email.trim();
      // Get passkey options
      const options = await api.auth.getPasskeyOptions(normalizedEmail);
      
      // Use WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: base64ToUint8Array(options.challenge),
          rpId: options.rpId,
          allowCredentials: options.allowCredentials?.map((c: any) => ({
            type: c.type,
            id: base64ToUint8Array(c.id),
          })),
          userVerification: 'preferred',
          timeout: 60000,
        },
      }) as PublicKeyCredential;
      
      if (!credential) {
        throw new Error('Passkey authentication cancelled');
      }
      
      const response = credential.response as AuthenticatorAssertionResponse;
      
      // Verify with server
      const res = await api.auth.verifyPasskey({
        email: normalizedEmail,
        credentialId: arrayBufferToBase64url(credential.rawId),
        authenticatorData: arrayBufferToBase64url(response.authenticatorData),
        clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
        signature: arrayBufferToBase64url(response.signature),
      });
      
      setAuth(res.token, res.user);
      navigate('/');
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled');
      } else {
        setError(err.message || 'Passkey authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Welcome back</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Sign in to continue to Moorly
        </p>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handlePasswordLogin} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="you@example.com"
            required
          />
        </div>
        
        {!showPasskey && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
              required={!showPasskey}
            />
          </div>
        )}
        
        <div className="pt-2 space-y-3">
          {showPasskey ? (
            <>
              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={loading || !email}
                className="btn btn-primary w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingSpinner />
                    Authenticating...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <PasskeyIcon className="w-5 h-5" />
                    Sign in with Passkey
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowPasskey(false)}
                className="btn btn-secondary w-full"
              >
                Use password instead
              </button>
            </>
          ) : (
            <>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingSpinner />
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowPasskey(true)}
                className="btn btn-secondary w-full"
              >
                <span className="flex items-center justify-center gap-2">
                  <PasskeyIcon className="w-5 h-5" />
                  Use Passkey
                </span>
              </button>
            </>
          )}
        </div>
      </form>
      
      <p className="text-center text-sm text-slate-600 dark:text-slate-400">
        Registration is coming soon.
      </p>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function PasskeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
    </svg>
  );
}
